/**
 * Quick Buy — in-sheet free-gift (GWP) picker.
 *
 * Kite's native widget only renders on product/cart pages, so it leaves our
 * mobile Add-to-Cart sheet (opened on collection/search pages) empty. This
 * renders our OWN picker UI here. Reading Kite's campaign and changing which
 * gift is in the cart both go through assets/gwp-claim.js — the shared layer
 * this and assets/cart-gwp.js hold in common, so the market gate and the
 * claim-through-Kite-only rule can't drift between the two surfaces. Visual
 * skin mirrors assets/cart-gwp.css so it matches the PDP/cart picker.
 * See memory: noted-kite-gwp-js-api.
 *
 * HARD RULE: claiming is gated on cart total ≥ the campaign threshold. Below it
 * the Function won't zero the gift, so claiming would CHARGE for it.
 */
(function () {
  'use strict';

  function esc(s) { return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;'); }

  // Shopper-facing wording from the theme's language file (snippets/js-strings.liquid).
  function str(key) {
    var s = window.notedStrings && window.notedStrings.gwp;
    return (s && s[key]) || '';
  }

  // currentColor glyphs (CSS sets the colour per state). Same shapes as cart-gwp.css.
  var SVG = {
    gift: '<svg class="qb-gwp__glyph" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 12 20 22 4 22 4 12"/><rect x="2" y="7" width="20" height="5"/><line x1="12" y1="22" x2="12" y2="7"/><path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"/><path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"/></svg>',
    plus: '<svg class="qb-gwp__glyph" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 5v14M5 12h14"/></svg>',
    chevron: '<svg class="qb-gwp__glyph" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 9l6 6 6-6"/></svg>',
    check: '<svg class="qb-gwp__glyph" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7"/></svg>'
  };

  var expanded = false; // preserved across re-renders within a sheet session

  function gwp() { return window.NotedGwpClaim; }

  function render(container) {
    if (!container) return;
    var api = gwp();
    var camp = api && api.campaign();
    if (!camp) { container.innerHTML = ''; container.hidden = true; return; }

    api.getCart()
      .then(function (cart) {
        var total = cart.total_price || 0;
        var unlocked = camp.thresholdPence > 0 && total >= camp.thresholdPence;

        // which gift variants are already claimed (this cart)
        var claimed = api.claimedIn(cart);
        var anyClaimed = Object.keys(claimed).length > 0;

        var rows = api.gifts(camp).map(function (o) {
          var isClaimed = !!claimed[o.variantId];
          var locked = !unlocked && !isClaimed;
          var cls = 'qb-gwp__claim' + (isClaimed ? ' is-claimed' : '') + (locked ? ' is-locked' : '');
          return '' +
            '<div class="qb-gwp__row">' +
              '<div class="qb-gwp__thumb">' + (o.image ? '<img src="' + esc(o.image) + '" alt="' + esc(o.title) + '" loading="lazy">' : '') + '</div>' +
              '<div class="qb-gwp__text">' +
                '<span class="qb-gwp__name">' + esc(o.title) + '</span>' +
                (o.sub ? '<span class="qb-gwp__sub">' + esc(o.sub) + '</span>' : '') +
                (o.variant ? '<span class="qb-gwp__variant">' + esc(o.variant) + '</span>' : '') +
              '</div>' +
              '<button type="button" class="' + cls + '" data-qb-gwp-claim data-variant-id="' + esc(o.variantId) + '"' +
                (locked ? ' aria-disabled="true"' : '') +
                ' aria-label="' + esc(str(isClaimed ? 'remove_gift' : 'choose_gift').replace('{title}', o.title)) + '">' +
                (isClaimed ? SVG.check : SVG.plus) +
              '</button>' +
            '</div>';
        }).join('');

        container.hidden = false;
        container.className = 'qb-gwp' + (unlocked ? ' is-unlocked' : '') + (expanded ? ' is-expanded' : '') + (anyClaimed ? ' is-claimed' : '');
        // Header copy + trailing glyph track state (Figma, parity with cart-gwp):
        // chosen → "Free gift added to your cart" + tick; else "Select your free
        // gift" + plus (chevron whenever the list is open).
        var headerTitle = esc(anyClaimed ? str('gift_added') : str('select_gift'));
        var headerGlyph = expanded ? SVG.chevron : (anyClaimed ? SVG.check : SVG.plus);
        container.innerHTML =
          '<button type="button" class="qb-gwp__header" data-qb-gwp-toggle aria-expanded="' + (expanded ? 'true' : 'false') + '">' +
            '<span class="qb-gwp__icon">' + SVG.gift + '</span>' +
            '<span class="qb-gwp__title">' + headerTitle + '</span>' +
            '<span class="qb-gwp__toggle">' + headerGlyph + '</span>' +
          '</button>' +
          '<div class="qb-gwp__list"' + (expanded ? '' : ' hidden') + '>' + rows + '</div>';
      })
      .catch(function () {});
  }

  /* ───────────── Claim / unclaim ───────────── */

  function handleClaim(btn) {
    if (btn.getAttribute('aria-disabled') === 'true' || btn.classList.contains('is-busy')) return;
    var container = btn.closest('[data-quick-buy-gwp]');
    var variantId = btn.getAttribute('data-variant-id');
    var api = gwp();
    var camp = api && api.campaign();
    if (!variantId || !camp) return;
    btn.classList.add('is-busy');

    var isClaimed = btn.classList.contains('is-claimed');
    var chain = isClaimed
      ? api.removeGiftLines([variantId])
      // Single-select campaign: clear any existing gift, then claim via Kite.
      : api.swapTo(variantId, camp);

    chain
      .then(function () { return new Promise(function (res) { setTimeout(res, 600); }); })
      .then(function () {
        document.dispatchEvent(new CustomEvent('cart:change'));
        if (container) render(container);
      })
      .catch(function (e) { console.error('[quick-buy-gwp] claim failed', e); if (container) render(container); });
  }

  /* Claim / swap / remove all live in assets/gwp-claim.js — including the rule
     that claiming goes through Kite's own function and nowhere else, and the
     market gate. Do not re-implement either here. */

  /* ───────────── Events ───────────── */

  document.addEventListener('click', function (e) {
    var toggle = e.target.closest('[data-qb-gwp-toggle]');
    if (toggle) {
      e.preventDefault();
      expanded = !expanded;
      var c1 = toggle.closest('[data-quick-buy-gwp]');
      if (c1) render(c1);
      return;
    }
    var claim = e.target.closest('[data-qb-gwp-claim]');
    if (claim) { e.preventDefault(); handleClaim(claim); }
  });

  // Re-render on any cart change while the picker is in the DOM (claim/add/remove).
  document.addEventListener('cart:change', function () {
    var c = document.querySelector('[data-quick-buy-gwp]');
    if (c && !c.hidden) render(c);
  });

  // Public entry — quick-buy.js calls this after injecting the sheet. Waits for
  // Kite's settings to be ready, then renders.
  function renderWhenReady(container, tries) {
    if (!container) return;
    tries = tries || 0;
    var api = gwp();
    if (api && api.campaign()) { render(container); return; }
    if (tries < 25) setTimeout(function () { renderWhenReady(container, tries + 1); }, 300);
  }

  window.NotedQuickBuyGwp = { render: renderWhenReady };
})();
